import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from '@kgc/i18n';
import type { SettlementDepositWire, SettlementPreviewWire } from '../../../api/wire-schemas';

/**
 * Elszámolás-előnézet panel (40-kör PV-BERLES-003 FE-1, PR-5 pin).
 *
 * Pinnelt szemantika:
 * - a tétel-`label` BE-generált — a FE rendereli, NEM gyárt szöveget;
 * - `vatRate === null` → »ÁFA egyeztetés alatt« badge, 0 számított érték (R-4);
 * - `nettable === false` kaució KIZÁRÓLAG a külön kártya-blokkban, sosem nettózva;
 * - vegyes bérlésen a cashFlow ÉS a cardFlow blokk együtt renderel;
 * - `forceImmediateSettle` (R-2 preAuth-hard-guard) → kényszer-»most« jelzés.
 * 0 FE-számítás: minden összeg a wire-ből jön, a komponens csak formáz.
 */
export interface SettlementPreviewPanelProps {
  preview: SettlementPreviewWire;
}

function DepositRow({ deposit }: { deposit: SettlementDepositWire }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center justify-between py-1 text-sm"
      data-testid={`settlement-deposit-${deposit.depositId}`}
    >
      <span className="text-muted-foreground flex items-center gap-2">
        {t(`rental.settlement.paymentMethod.${deposit.paymentMethod}`)}
        {deposit.forceImmediateSettle && (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300"
            data-testid={`settlement-force-immediate-${deposit.depositId}`}
          >
            {t('rental.settlement.forceImmediate')}
          </span>
        )}
      </span>
      <span className="font-medium tabular-nums">{formatCurrency(deposit.amount)}</span>
    </div>
  );
}

export function SettlementPreviewPanel({ preview }: SettlementPreviewPanelProps) {
  const { t } = useTranslation();

  const cardDeposits = preview.deposits.filter(d => !d.nettable);

  return (
    <div className="space-y-3 rounded-lg border p-4" data-testid="settlement-preview-panel">
      <h4 className="font-medium">{t('rental.settlement.previewTitle')}</h4>

      {/* 41-kör FE-3 ④: üres tétel-lista → explicit „nincs levonás" empty-state
          (a néma hiány kétértelmű: „nem töltött be?" vs „tényleg nincs") */}
      {preview.lines.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="settlement-lines-empty">
          {t('rental.noDeductions')}
        </p>
      )}

      {/* Végszámla-tétel-előkép — a label BE-generált */}
      {preview.lines.length > 0 && (
        <div className="space-y-1" data-testid="settlement-lines">
          <p className="text-sm font-medium text-muted-foreground">
            {t('rental.settlement.linesTitle')}
          </p>
          {preview.lines.map((line, idx) => (
            <div
              key={`${line.kind}-${line.rentalItemId ?? 'rental'}-${idx}`}
              className="flex items-center justify-between text-sm"
              data-testid={`settlement-line-${idx}`}
            >
              <span className="text-muted-foreground flex items-center gap-2 truncate mr-2">
                {line.label}
                {line.vatRate === null && (
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 shrink-0"
                    data-testid={`settlement-vat-pending-${idx}`}
                  >
                    {t('rental.settlement.vatPending')}
                  </span>
                )}
              </span>
              <span className="font-medium shrink-0 tabular-nums">
                {formatCurrency(line.amount)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* KP-ág (nettózott) */}
      <div
        className="space-y-1 rounded-md bg-muted/40 p-3 text-sm"
        data-testid="settlement-cash-flow"
      >
        <p className="font-medium">{t('rental.settlement.cashTitle')}</p>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('rental.settlement.deductionsTotal')}</span>
          <span className="tabular-nums">{formatCurrency(preview.cashFlow.deductionsTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('rental.settlement.depositApplied')}</span>
          <span className="tabular-nums">{formatCurrency(preview.cashFlow.depositApplied)}</span>
        </div>
        {preview.cashFlow.refundToCustomer > 0 && (
          <div className="flex items-baseline justify-between font-medium text-green-700 dark:text-green-300">
            <span>{t('rental.settlement.refundToCustomer')}</span>
            <span className="text-base font-bold tabular-nums" data-testid="settlement-refund">
              {formatCurrency(preview.cashFlow.refundToCustomer)}
            </span>
          </div>
        )}
        {preview.cashFlow.payableByCustomer > 0 && (
          <div className="flex items-baseline justify-between font-medium text-red-600">
            <span>{t('rental.settlement.payableByCustomer')}</span>
            <span className="text-base font-bold tabular-nums" data-testid="settlement-payable">
              {formatCurrency(preview.cashFlow.payableByCustomer)}
            </span>
          </div>
        )}
      </div>

      {/* Kártya-ág — KÜLÖN blokk, SOSEM nettózva (pin ③) */}
      {preview.cardFlow !== null && (
        <div
          className="space-y-1 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-sm"
          data-testid="settlement-card-flow"
        >
          <p className="font-medium">{t('rental.settlement.cardTitle')}</p>
          <p className="text-xs text-muted-foreground">{t('rental.settlement.cardNotNetted')}</p>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('rental.settlement.invoiceTotal')}</span>
            <span className="tabular-nums">{formatCurrency(preview.cardFlow.invoiceTotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('rental.settlement.myposCapture')}</span>
            <span className="tabular-nums">
              {formatCurrency(preview.cardFlow.myposCaptureTotal)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('rental.settlement.myposRefund')}</span>
            <span className="tabular-nums">
              {formatCurrency(preview.cardFlow.myposRefundTotal)}
            </span>
          </div>
          {cardDeposits.length > 0 && (
            <div className="pt-1" data-testid="settlement-card-deposits">
              {cardDeposits.map(d => (
                <DepositRow key={d.depositId} deposit={d} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
